import { useEffect, useRef } from 'react';

type TUseTrackedImpressionOptions = {
  enabled?: boolean;
  minVisibleMs?: number;
  onImpression: () => void;
  threshold?: number;
};

/**
 * Tracks a one-shot impression after an element stays sufficiently visible
 * in the active tab for a minimum duration.
 */
export function useTrackedImpression<TElement extends HTMLElement>({
  enabled = true,
  minVisibleMs = 750,
  onImpression,
  threshold = 0.5
}: TUseTrackedImpressionOptions) {
  const elementRef = useRef<TElement>(null);
  const hasTrackedRef = useRef(false);
  const isEligibleVisibleRef = useRef(false);
  const impressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onImpressionRef = useRef(onImpression);

  useEffect(() => {
    onImpressionRef.current = onImpression;
  }, [onImpression]);

  useEffect(() => {
    if (!enabled || hasTrackedRef.current) {
      return;
    }

    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const element = elementRef.current;
    if (!element) {
      return;
    }

    let observer: IntersectionObserver | null = null;

    const clearImpressionTimer = () => {
      if (!impressionTimerRef.current) {
        return;
      }

      clearTimeout(impressionTimerRef.current);
      impressionTimerRef.current = null;
    };

    const fireImpression = () => {
      clearImpressionTimer();

      if (hasTrackedRef.current) {
        return;
      }

      hasTrackedRef.current = true;
      observer?.disconnect();
      onImpressionRef.current();
    };

    const startImpressionTimer = () => {
      if (impressionTimerRef.current || document.hidden || hasTrackedRef.current || !isEligibleVisibleRef.current) {
        return;
      }

      impressionTimerRef.current = setTimeout(() => {
        impressionTimerRef.current = null;

        if (document.hidden || !isEligibleVisibleRef.current || hasTrackedRef.current) {
          return;
        }

        fireImpression();
      }, minVisibleMs);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearImpressionTimer();
        return;
      }

      if (isEligibleVisibleRef.current) {
        startImpressionTimer();
      }
    };

    observer = new IntersectionObserver(
      (entries) => {
        const isEligibleVisible = entries.some((entry) => entry.intersectionRatio >= threshold);
        isEligibleVisibleRef.current = isEligibleVisible;

        if (isEligibleVisible) {
          startImpressionTimer();
        } else {
          clearImpressionTimer();
        }
      },
      { threshold: [0, threshold] }
    );

    observer.observe(element);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isEligibleVisibleRef.current = false;
      clearImpressionTimer();
      observer?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, minVisibleMs, threshold]);

  return elementRef;
}
